import { useState, useEffect, useRef } from "react";
import { Megaphone, Search, Loader2, UserRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { STAGES, STAGE_KEYS, GRADES_BY_STAGE, sectionsForGrade } from "../lib/constants";
import { pickupDutySearchStudents, pickupDutyCall } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function PickupDuty() {
  const { user } = useAuth();
  const [mode, setMode] = useState("browse"); // browse | search
  const [stage, setStage] = useState("");
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [nameQuery, setNameQuery] = useState("");
  const [students, setStudents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(null);

  const stageRef = useRef(); const gradeRef = useRef(); const sectionRef = useRef(); const modeRef = useRef(); const qRef = useRef();
  stageRef.current = stage; gradeRef.current = grade; sectionRef.current = section; modeRef.current = mode; qRef.current = nameQuery;

  const allowedStages = user?.role === "admin" || user?.role === "super_admin" || !user?.assigned_stages?.length
    ? STAGE_KEYS : STAGE_KEYS.filter(s => user.assigned_stages.includes(s));
  const grades = stage ? GRADES_BY_STAGE[stage] : [];
  const sections = sectionsForGrade(grade);

  const doSearch = async (silent = false) => {
    const m = modeRef.current;
    if (m === "browse" && !(stageRef.current && gradeRef.current && sectionRef.current)) {
      if (!silent) toast.error("اختر المرحلة والصف والفصل");
      return;
    }
    if (m === "search" && !qRef.current.trim()) {
      if (!silent) toast.error("اكتب اسم الطالب للبحث");
      return;
    }
    if (!silent) setLoading(true);
    try {
      const params = m === "browse"
        ? { stage: stageRef.current, grade: gradeRef.current, section: sectionRef.current }
        : { q: qRef.current.trim() };
      const data = await pickupDutySearchStudents(params);
      setStudents(data);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if ((modeRef.current === "browse" && stageRef.current && gradeRef.current && sectionRef.current) ||
          (modeRef.current === "search" && qRef.current.trim())) {
        doSearch(true);
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const doCall = async (student) => {
    setCalling(student.id);
    try {
      const req = await pickupDutyCall(student.id);
      toast.success(`تم استدعاء ${student.name} بنجاح — رقم الطلب: ${req.request_number}`);
      await doSearch(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الاستدعاء");
    } finally {
      setCalling(null);
    }
  };

  return (
    <div>
      <PageHeader title="المناوب" subtitle="استدعاء الطالب مباشرة بالنيابة عن ولي الأمر (للحالات التي لا يتوفر فيها جوال أو إنترنت)" icon={Megaphone} />

      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button onClick={() => { setMode("browse"); setStudents(null); }} className={`px-4 py-2.5 text-sm font-cairo font-bold border-b-2 ${mode === "browse" ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500"}`}>تصفح فصل</button>
        <button onClick={() => { setMode("search"); setStudents(null); }} className={`px-4 py-2.5 text-sm font-cairo font-bold border-b-2 ${mode === "search" ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500"}`}>بحث بالاسم</button>
      </div>

      <Card className="p-4 sm:p-5 border-slate-200 mb-5">
        {mode === "browse" ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>المرحلة</Label>
              <Select value={stage} onValueChange={(v) => { setStage(v); setGrade(""); setSection(""); setStudents(null); }}>
                <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder="اختر" /></SelectTrigger>
                <SelectContent dir="rtl">{allowedStages.map(k => <SelectItem key={k} value={k}>{STAGES[k]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>الصف</Label>
              <Select value={grade} onValueChange={(v) => { setGrade(v); setSection(""); setStudents(null); }} disabled={!stage}>
                <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder={stage ? "اختر" : "..."} /></SelectTrigger>
                <SelectContent dir="rtl">{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>الفصل</Label>
              <Select value={section} onValueChange={setSection} disabled={!grade}>
                <SelectTrigger className="h-10" dir="rtl"><SelectValue placeholder={grade ? "اختر" : "..."} /></SelectTrigger>
                <SelectContent dir="rtl">{sections.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label>اسم الطالب</Label>
            <Input value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} placeholder="اكتب جزء من اسم الطالب" className="h-10" onKeyDown={(e) => e.key === "Enter" && doSearch()} />
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button onClick={() => doSearch()} disabled={loading} className="h-10 px-5 bg-sky-700 hover:bg-sky-800 text-white gap-2">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} بحث
          </Button>
        </div>
      </Card>

      {students && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {students.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400">لا يوجد نتائج.</div>
          )}
          {students.map((s) => {
            const req = s.pickup_request;
            const called = !!req;
            const exited = called && (req.status === "exited" || req.status === "on_the_way");
            const waiting = called && !exited;
            return (
              <div key={s.id} className={`rounded-2xl border p-4 ${waiting ? "bg-rose-50 border-rose-300" : exited ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                <div className={`w-14 h-14 rounded-xl mx-auto flex items-center justify-center mb-2 ${waiting ? "bg-rose-100" : exited ? "bg-emerald-100" : "bg-slate-50"}`}>
                  <UserRound size={26} className={waiting ? "text-rose-600" : exited ? "text-emerald-600" : "text-slate-400"} />
                </div>
                <div className={`font-cairo font-bold text-sm text-center ${waiting ? "text-rose-700" : exited ? "text-emerald-700" : "text-slate-800"}`}>
                  {waiting && "🔴 "}{exited && "🟢 "}{s.name}
                </div>
                <div className="text-[11px] text-slate-500 text-center mt-1">{s.grade} {s.section && `— ${s.section}`}</div>
                {!called ? (
                  <Button onClick={() => doCall(s)} disabled={calling === s.id} className="w-full h-9 mt-3 gap-1.5 bg-sky-700 hover:bg-sky-800 text-white text-xs">
                    {calling === s.id ? <Loader2 className="animate-spin" size={14} /> : <Megaphone size={14} />} استدعاء
                  </Button>
                ) : (
                  <div className="mt-3 text-center text-[11px] font-bold text-slate-500">
                    {waiting ? "مطلوب استدعاؤه" : "خرج من الفصل"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
